import React, {
  type FunctionComponent,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
} from 'react';

import { type JsonRpcProvider } from '@ethersproject/providers';
import { useEthers } from '@usedapp/core';
import WalletConnectProvider from '@walletconnect/web3-provider';

import Loader from '@bitcoin-portal/verse-web-components/dist/Loader';

import { VERSE_ETHEREUM_TOKEN_CONTRACT } from '@context/constants';
import { useBcomAccounts } from '@context/providers/BcomAccountsProvider';

import {
  DEBRIDGE_EVM_WALLETS,
  DEBRIDGE_SOLANA_WALLETS,
} from '@views/Swap/context/constants';

import { useQuery } from '@helpers/index';
import { isMetaMask } from '@helpers/web3';

import { DlnLoader, DlnWrapper } from './styled';

const AFFILIATE_CODE = '30064';
const AFFILIATE_FEE_RECIPIENT = '0xf5A25fDC50193c6064d0374C12454f33510c2148';

export const DlnWidget: FunctionComponent = () => {
  const query = useQuery();
  const { bcomAccounts } = useBcomAccounts();

  const { account, chainId, library, switchNetwork } = useEthers();
  const [widgetLoaded, setWidgetLoaded] = useState(false);
  const [widget, setWidget] = useState<any>(null);
  const [fromChainOverride, setFromChainOverride] = useState<number | null>(
    null,
  );
  const [toChainOverride, setToChainOverride] = useState<number | null>(null);
  const [fromTokenOverride, setFromTokenOverride] = useState<string | null>(
    null,
  );
  const [toTokenOverride, setToTokenOverride] = useState<string | null>(null);

  const disabledWallets = useMemo(() => {
    const list = DEBRIDGE_SOLANA_WALLETS;
    if (query.get('origin') === 'wallet') {
      list.push(...DEBRIDGE_EVM_WALLETS);
    }
    return list;
  }, [query]);

  useLayoutEffect(() => {
    const queryFromChain = query.get('fromChain');
    const queryToChain = query.get('toChain');

    const queryFromToken = query.get('fromToken');
    const queryToToken = query.get('toToken');

    if (queryFromChain) setFromChainOverride(parseInt(queryFromChain, 10));
    if (queryToChain) setToChainOverride(parseInt(queryToChain, 10));
    if (queryFromToken) setFromTokenOverride(queryFromToken);
    if (queryToToken) setToTokenOverride(queryToToken);
  }, [query]);

  useEffect(() => {
    const widgetCheck = setInterval(() => {
      if (window?.deBridge) {
        setWidgetLoaded(true);
      }
    }, 1000);

    return () => {
      clearInterval(widgetCheck);
    };
  }, []);

  useEffect(() => {
    if (!widgetLoaded) return;
    const wid = window?.deBridge?.widget?.({
      v: '1',
      element: 'debridgeWidget',
      title: 'Swap',
      description: '',
      width: '600',
      height: '800',
      r: AFFILIATE_CODE,
      affiliateFeePercent: '1',
      affiliateFeeRecipient: AFFILIATE_FEE_RECIPIENT,
      supportedChains:
        '{"inputChains":{"1":"all","137":"all","43114":"all","56":"all"},"outputChains":{"1":"all","137":"all","43114":"all","56":"all"}}',
      inputChain: fromChainOverride ?? 1,
      outputChain: toChainOverride ?? 1,
      inputCurrency: fromTokenOverride ?? '',
      outputCurrency: toTokenOverride ?? VERSE_ETHEREUM_TOKEN_CONTRACT,
      address: '',
      showSwapTransfer: true,
      amount: '',
      outputAmount: '',
      isAmountFromNotModifiable: false,
      isAmountToNotModifiable: false,
      lang: 'en',
      mode: 'deswap',
      isEnableCalldata: false,
      styles:
        'eyJhcHBCYWNrZ3JvdW5kIjoiIzBhMGEyZSIsImJvcmRlclJhZGl1cyI6MjAsImJvcmRlckNvbG9yIjoiIzAzMGMxNCIsImZvcm1Db250cm9sQmciOiIjMGYxODIzIiwiY29udHJvbEJvcmRlciI6IiMzMTNlNTciLCJwcmltYXJ5IjoiIzAwODVmZiIsInNlY29uZGFyeSI6IiNmZmZmZmYiLCJmb250Q29sb3IiOiIjZmZmZmZmIiwiZm9udEZhbWlseSI6IkJhcmxvdyIsInByaW1hcnlCdG5CZyI6ImxpbmVhci1ncmFkaWVudCg5MGRlZywgIzI1NjNlYiAwJSwgIzkzMzNlYSAxMDAlKSIsInByaW1hcnlCdG5CZ0hvdmVyIjoiIzNiOTdmZiIsInNlY29uZGFyeUJ0bkJnIjoiIzg4OTViMSIsImJ0blBhZGRpbmciOnsidG9wIjo4LCJyaWdodCI6MTYsImJvdHRvbSI6OCwibGVmdCI6MTZ9LCJidG5Gb250V2VpZ2h0Ijo2MDB9',
      theme: 'dark',
      isHideLogo: false,
      logo: '',
      disabledWallets,
      disabledElements: ['Points'],
    });

    setWidget(wid);
  }, [widgetLoaded]);

  useEffect(() => {
    if (window && library) {
      const { provider } = library as unknown as JsonRpcProvider & {
        provider: WalletConnectProvider;
      };

      if (!account) {
        if (widget) {
          widget.then((wid: any) => {
            wid.disconnect();
          });
        }
      }
      if (provider && widget) {
        widget.then(async (wid: any) => {
          const evmSettings = isMetaMask(library as JsonRpcProvider)
            ? {
                name: 'MetaMask',
                imageSrc:
                  'https://verse.bitcoin.com/images/uploads/metamask.png',
              }
            : {
                name: 'WalletConnect',
                imageSrc:
                  'https://verse.bitcoin.com/images/uploads/walletconnect.png',
              };

          wid.setExternalEVMWallet({
            provider,
            ...evmSettings,
          });

          if (bcomAccounts) {
            wid.on('inputChainChanged', async (event: any, params: any) => {
              const prevChain = chainId ?? 1;
              const { chainId: newChain } = params ?? { chainId: 1 };
              await switchNetwork(newChain);

              wid.setReceiverAddress(bcomAccounts[prevChain] ?? account);
            });

            wid.on('outputChainChanged', (event: any, params: any) => {
              const { chainId: newChain } = params ?? { chainId: 1 };
              wid.setReceiverAddress(bcomAccounts[newChain] ?? account);
            });
          }
        });
      }
    }
  }, [account, bcomAccounts, library, widget]);

  if (!widgetLoaded) {
    return (
      <DlnLoader>
        <Loader />
      </DlnLoader>
    );
  }

  return (
    <DlnWrapper>
      <div id="debridgeWidget" />
    </DlnWrapper>
  );
};
